import type { ElementType } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';

import { useRef, useEffect } from 'react';
import { m, animate, useInView, useTransform, useMotionValue } from 'framer-motion';

import Typography from '@mui/material/Typography';

// ----------------------------------------------------------------------

/**
 * 화면에 들어오면 `from` 에서 `to` 까지 세어 올리는 숫자.
 *
 * ⚠️ **필수 프롭은 `to` 하나다.**
 *
 * ⚠️ `unit` 을 주면 **줄임(k·m·b)을 하지 않고 원본 값**을 센다. 안 주면 1,000 이상은
 * 줄여 세고 단위를 뒤에 붙인다.
 */
export type AnimateCountUpProps = {
  /** 세어 올릴 목표 값 */
  to: number;
  sx?: SxProps<Theme>;
  /** 시작 값. 기본 `0` */
  from?: number;
  /** 소수 자리(정수면 무시). 기본 `0` */
  toFixed?: number;
  /** 한 번만 셀지. 기본 `true` */
  once?: boolean;
  /** 애니메이션 길이(초). 기본 `2` */
  duration?: number;
  /** `useInView` 판정 비율. 기본 `0.5` */
  amount?: number | 'some' | 'all';
  /** 뒤에 붙일 단위. 주면 줄임을 끄고 원본 값을 센다 */
  unit?: string;
  /** 바깥 `Typography` 의 태그. 기본 `'p'` */
  component?: ElementType;
};

export function AnimateCountUp({
  to,
  sx,
  from = 0,
  toFixed = 0,
  once = true,
  duration = 2,
  amount = 0.5,
  unit: unitProp,
  component = 'p',
  ...other
}: AnimateCountUpProps) {
  const countRef = useRef(null);

  const shortNumber = shortenNumber(to);

  const startCount = useMotionValue(from);
  const endCount = unitProp ? to : (shortNumber ? shortNumber.value : to); // unit이 명시되면 원본 값 사용

  const unit = unitProp ?? shortNumber?.unit;

  const inView = useInView(countRef, { once, amount });

  const rounded = useTransform(startCount, (latest) =>
    latest.toFixed(isFloat(latest) ? toFixed : 0)
  );

  useEffect(() => {
    if (inView) {
      animate(startCount, endCount, { duration });
    }
  }, [duration, endCount, inView, startCount]);

  return (
    <Typography
      component={component}
      sx={[
        {
          p: 0,
          m: 0,
          display: 'inline-flex',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <m.span ref={countRef}>{rounded}</m.span>
      {unit}
    </Typography>
  );
}

// ----------------------------------------------------------------------

function isFloat(n: number) {
  return typeof n === 'number' && !Number.isInteger(n);
}

function shortenNumber(value: number) {
  if (value >= 1e9) {
    return { unit: 'b', value: value / 1e9 };
  }
  if (value >= 1e6) {
    return { unit: 'm', value: value / 1e6 };
  }
  if (value >= 1e3) {
    return { unit: 'k', value: value / 1e3 };
  }
  return undefined;
}
